import isString from 'lodash/isString'
import noop from 'lodash/noop'
import React, {memo, useEffect, useState} from 'react'

import {getSvgFromResponse} from '../../utils'

interface SvgProps {
  src: string
  className?: string
  role?: string
  Tag?: keyof JSX.IntrinsicElements
}

type SvgAssetCache = {
  [key: string]: Promise<string>
}

export const matchEncodedSvg = (string = ''): RegExpMatchArray | null =>
  string.match(/data:image\/svg[^,]*?(base64)?,(.*)/)

/**
 * Helper function to create a cache of fetched SVG assets.
 */
const createCache = () => {
  const cache: SvgAssetCache = {}
  return function getSvg(url: string) {
    if (!Object.prototype.hasOwnProperty.call(cache, url)) {
      const val = fetch(url).then(getSvgFromResponse).catch(noop)
      if (val) {
        cache[url] = val
      }
    }
    return cache[url]
  }
}

export const cache = createCache()

const Svg: React.FC<SvgProps> = ({className, src, Tag = 'div', ...rest}) => {
  const [svg, setSvg] = useState('')

  useEffect(() => {
    function load() {
      // don't bother loading unless we have a real string
      if (!isString(src) || !src.length) {
        return
      }

      const matches = matchEncodedSvg(src)
      if (matches) {
        // we have a base64 or utf8 encoded SVG
        setSvg(matches[1] ? atob(matches[2]) : decodeURIComponent(matches[2]))
        return
      }

      // we assume src is a url pointing to an SVG
      cache(src).then(setSvg)
    }
    load()
  }, [src])

  return <Tag className={className} dangerouslySetInnerHTML={{__html: svg}} {...rest} />
}

export default memo(Svg)
